import type { EngineFactEnvelope } from "../report/contracts";
import { asRecord, asString } from "./engine-facts";
import type {
  VietnameseRecommendationCoverage,
  VietnameseRecommendationTopic,
} from "./types";

// "Màu sắc hợp mệnh" chưa được engine nào tính (không nằm trong ranh giới đã
// ký của ruleset v1 Phase 5) — luôn còn trong danh sách chờ cho tới khi có
// engine thật sự tính ra màu sắc.
const NEVER_COMPUTED_TOPICS_VI: readonly string[] = ["Màu sắc hợp mệnh"];

function fengShuiTopics(
  envelopes: readonly EngineFactEnvelope[],
): readonly VietnameseRecommendationTopic[] {
  const envelope = envelopes.find((item) => item.engine === "feng_shui");
  if (!envelope) return [];

  const batTrach = asRecord(envelope.facts.batTrach);
  const huyenKhong = asRecord(envelope.facts.huyenKhong);
  const topics: VietnameseRecommendationTopic[] = [];

  if (batTrach) {
    const danhGia = asString(batTrach.danhGiaHuongHienTaiVi);
    const khuyenNghi = asString(batTrach.khuyenNghiVi);
    topics.push({
      topicVi: "Hướng nhà",
      evidenceLevel: "kỹ_thuật",
      statusVi: [danhGia, khuyenNghi].filter(Boolean).join(" ") || "—",
    });
  }

  if (batTrach || huyenKhong) {
    const parts = [
      batTrach ? asString(batTrach.khuyenNghiVi) : "",
      huyenKhong ? asString(huyenKhong.khuyenNghiVi) : "",
    ].filter(Boolean);
    topics.push({
      topicVi: "Phong Thủy",
      evidenceLevel: "kỹ_thuật",
      statusVi:
        parts.join(" · ") || "Đã có dữ kiện, xem chi tiết ở mục Phong Thủy.",
    });
  }

  return topics;
}

function nameWuxingTopics(
  envelopes: readonly EngineFactEnvelope[],
): readonly VietnameseRecommendationTopic[] {
  const envelope = envelopes.find(
    (item) => item.engine === "name_phone_wuxing",
  );
  if (!envelope) return [];

  const hanTruyenThong = asRecord(envelope.facts.hanTruyenThong);
  const thanSoTay = asRecord(envelope.facts.thanSoTay);
  const topics: VietnameseRecommendationTopic[] = [];

  if (hanTruyenThong && hanTruyenThong.status === "ok") {
    topics.push({
      topicVi: "Đổi tên",
      evidenceLevel: "kỹ_thuật",
      statusVi:
        asString(hanTruyenThong.khuyenNghiVi) ||
        "Tên hiện tại đã hài hòa Ngũ Hành, chưa cần đổi.",
    });
  }

  if (hanTruyenThong || thanSoTay) {
    const parts = [
      hanTruyenThong && hanTruyenThong.status === "ok"
        ? asString(hanTruyenThong.khuyenNghiVi)
        : "",
      thanSoTay ? asString(thanSoTay.khuyenNghiVi) : "",
    ].filter(Boolean);
    topics.push({
      topicVi: "Tên và số Ngũ Hành",
      evidenceLevel: "kỹ_thuật",
      statusVi:
        parts.join(" · ") ||
        "Đã có dữ kiện, xem chi tiết ở mục Tên và số Ngũ Hành.",
    });
  }

  return topics;
}

export function buildRecommendationCoverage(
  envelopes: readonly EngineFactEnvelope[],
): VietnameseRecommendationCoverage {
  const topics = Object.freeze([
    ...fengShuiTopics(envelopes),
    ...nameWuxingTopics(envelopes),
  ]);

  const pending = [...NEVER_COMPUTED_TOPICS_VI];
  if (!envelopes.some((item) => item.engine === "feng_shui")) {
    pending.unshift("Phong Thủy");
  }
  if (!envelopes.some((item) => item.engine === "name_phone_wuxing")) {
    pending.unshift("Tên và số Ngũ Hành");
  }

  return Object.freeze({
    headingVi: "Diễn giải kỹ thuật",
    technicalSummaryVi:
      "Tóm tắt đúng dữ kiện đã tính từ các phương pháp sẵn sàng; chưa có luận giải cá nhân hóa hay dự đoán ngoài dữ kiện kỹ thuật.",
    actionPlanStatusVi:
      "Kế hoạch hành động 30/90 ngày chờ chuyên gia và kho tư liệu đã duyệt.",
    topics,
    pendingTopicsNoteVi: `Sẽ có khi mở khoá: ${pending.join(", ")}.`,
  });
}
